import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { initials, socialUrl } from "../helpers";
import { Icon } from "../icons";
import { CARD_HOVER, Container, EditorImagePlaceholder, Img, MultiLine, Ornament, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type Item = SectionProps<"team">["node"]["content"]["items"][number];

function Instagram({ ctx, item, i, className }: { ctx: RenderContext; item: Item; i: number; className?: string }) {
  const href = socialUrl("instagram", item.instagram);
  if (!href) return null;
  return (
    <SiteLink
      ctx={ctx}
      href={href}
      fallback="none"
      fieldPath={`content.items.${i}.instagram`}
      ariaLabel={`Instagram ${item.name}`}
      className={cx("inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-site-primary underline-offset-4 hover:underline", className)}
    >
      <Icon name="instagram" className="size-4" />
      <span>@{item.instagram}</span>
    </SiteLink>
  );
}

function Portrait({ ctx, item, i, className, sizes }: { ctx: RenderContext; item: Item; i: number; className: string; sizes: string }) {
  return (
    <div className={cx("overflow-hidden bg-site-surface", className)}>
      {item.image ? (
        <Img ctx={ctx} image={item.image} sizes={sizes} target={960} path={`content.items.${i}.image`} className="transition-transform duration-700 group-hover:scale-[1.04] motion-reduce:transition-none" />
      ) : ctx.editor ? (
        <EditorImagePlaceholder ctx={ctx} path={`content.items.${i}.image`} />
      ) : (
        <div aria-hidden="true" className="baze-heading grid h-full w-full place-items-center bg-site-primary/10 text-5xl text-site-primary/60">
          {initials(item.name)}
        </div>
      )}
    </div>
  );
}

/** Two people side by side with an ampersand: the bride and groom of a wedding invitation. */
function Couple({ node, ctx }: SectionProps<"team">) {
  const c = node.content;
  const pair = c.items.slice(0, 2);
  const rest = c.items.slice(2);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
        <div className={cx(STACK, "grid items-center gap-10 @3xl:grid-cols-[1fr_auto_1fr] @3xl:gap-8")}>
          {pair.map((item, i) => (
            <div key={i} className={cx("contents")}>
              {i === 1 ? (
                <div aria-hidden="true" className="baze-reveal-pop flex flex-col items-center gap-3 text-site-primary">
                  <span className="hidden h-16 w-px bg-site-border @3xl:block" />
                  <span className="baze-heading text-6xl leading-none @3xl:text-7xl">&amp;</span>
                  <span className="hidden h-16 w-px bg-site-border @3xl:block" />
                </div>
              ) : null}
              <figure className={cx("group text-center", i === 0 ? "baze-reveal-right" : "baze-reveal-left")}>
                <div className="relative mx-auto w-full max-w-[13rem] @md:max-w-[15rem] @3xl:max-w-[17rem]">
                  <div aria-hidden="true" className="absolute -inset-2.5 rounded-t-full rounded-b-site-lg border border-site-primary/30" />
                  <Portrait ctx={ctx} item={item} i={i} className="relative aspect-[3/4] rounded-t-full rounded-b-site-lg shadow-site-md" sizes="272px" />
                </div>
                <figcaption className="mt-8">
                  <h3 className="baze-heading text-[2rem] leading-tight @3xl:text-[2.25rem]" {...field(ctx, `content.items.${i}.name`)}>
                    {item.name}
                  </h3>
                  {item.role ? (
                    <p className="mt-2 text-xs font-semibold tracking-[0.18em] text-site-primary uppercase" {...field(ctx, `content.items.${i}.role`)}>
                      {item.role}
                    </p>
                  ) : null}
                  <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mx-auto mt-3 max-w-xs leading-relaxed text-site-muted" />
                  <Instagram ctx={ctx} item={item} i={i} className="mt-2" />
                </figcaption>
              </figure>
            </div>
          ))}
        </div>
        {rest.length > 0 ? (
          <>
            <div className="mt-14 flex justify-center">
              <Ornament />
            </div>
            <ul className={cx(STAGGER, "mt-10 grid grid-cols-2 gap-6 @3xl:grid-cols-4")}>
              {rest.map((item, j) => (
                <li key={j} className="text-center">
                  <p className="font-semibold text-site-text" {...field(ctx, `content.items.${j + 2}.name`)}>
                    {item.name}
                  </p>
                  {item.role ? <p className="text-sm text-site-muted">{item.role}</p> : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/** The people behind the business: stylists, consultants, founders, crew. */
export function Team(props: SectionProps<"team">) {
  const { node, ctx } = props;
  if (node.variant === "couple") return <Couple {...props} />;
  const c = node.content;
  const n = c.items.length;
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align={n < 3 ? "center" : "left"} />
        <ul className={cx(STACK, STAGGER, "grid gap-5 @3xl:gap-6", n === 1 ? "mx-auto max-w-sm" : n === 2 ? "mx-auto max-w-3xl grid-cols-2" : "grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4")}>
          {c.items.map((item, i) => (
            <li key={i} className={cx("group flex flex-col overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm", CARD_HOVER)}>
              <Portrait ctx={ctx} item={item} i={i} className="aspect-[4/5]" sizes="(min-width: 1024px) 280px, (min-width: 768px) 33vw, 50vw" />
              <div className="flex flex-1 flex-col p-4 @3xl:p-5">
                <h3 className="baze-heading text-lg leading-snug @3xl:text-xl" {...field(ctx, `content.items.${i}.name`)}>
                  {item.name}
                </h3>
                {item.role ? (
                  <p className="mt-1 text-sm font-medium text-site-primary" {...field(ctx, `content.items.${i}.role`)}>
                    {item.role}
                  </p>
                ) : null}
                <MultiLine ctx={ctx} text={item.description} path={`content.items.${i}.description`} className="mt-2 text-sm leading-relaxed text-site-muted" />
                <Instagram ctx={ctx} item={item} i={i} className="mt-auto pt-2" />
              </div>
            </li>
          ))}
        </ul>
      </Container>
    </SectionShell>
  );
}
